import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import UITag from 'UIComponents/tag/UITag';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import * as PlanType from 'ecosystem-marketplace-core/constants/pricings/PlanType';
import { getAssetBusinessTypeI18nKey, getAssetPageTypeI18nKey, getAssetPageFeatureI18nKey, getTierI18nKey // @ts-expect-error module is untyped
} from 'ecosystem-marketplace-core/utils/i18n'; // @ts-expect-error module is untyped

import MobileTagButton from 'ecosystem-marketplace-core/js/components/mobile/MobileTagButton';
import { useModuleCategories } from '../../../modules/hooks/useModuleCategories';

function CategoryTag({
  value
}) {
  const categories = useModuleCategories();

  try {
    const displayName = categories.getCategoryDisplayName(value);
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-assets-ui.assets.filters.categories.tag",
      options: {
        value: displayName
      }
    });
  } catch (_unused) {
    return null;
  }
}

function PlanTypeTag({
  value
}) {
  let tagValue;

  if (value === PlanType.FREE) {
    tagValue = unescapedText('marketplace-assets-ui.assets.filters.price.free');
  } else if (value === PlanType.ONE_TIME) {
    tagValue = unescapedText('marketplace-assets-ui.assets.filters.price.paid');
  } else {
    // TODO min/max price
    tagValue = unescapedText('marketplace-assets-ui.assets.filters.price.custom');
  }

  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "marketplace-assets-ui.assets.filters.price.tag",
    options: {
      value: tagValue
    }
  });
}

const FilterTagMap = {
  businessTypes: ({
    value
  }) => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "marketplace-assets-ui.assets.filters.businessType.tag",
    options: {
      value: unescapedText(getAssetBusinessTypeI18nKey(value))
    }
  }),
  pageTypes: ({
    value
  }) => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "marketplace-assets-ui.assets.filters.pageTypes.tag",
    options: {
      value: unescapedText(getAssetPageTypeI18nKey(value))
    }
  }),
  pageFeatures: ({
    value
  }) => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "marketplace-assets-ui.assets.filters.pageFeatures.tag",
    options: {
      value: unescapedText(getAssetPageFeatureI18nKey(value))
    }
  }),
  contentTypes: ({
    value
  }) => {
    const contentTypeI18nKey = `contentUtils.contentTypes.${value}`;
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-assets-ui.assets.filters.contentTypes.tag",
      options: {
        value: isValidI18nKey(contentTypeI18nKey) ? unescapedText(contentTypeI18nKey) : value
      }
    });
  },
  categories: ({
    value
  }) => /*#__PURE__*/_jsx(CategoryTag, {
    value: value
  }),
  planType: ({
    value
  }) => /*#__PURE__*/_jsx(PlanTypeTag, {
    value: value
  }),
  priceRange: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "marketplace-assets-ui.assets.filters.price.tag",
    options: {
      value: unescapedText('marketplace-assets-ui.assets.filters.price.custom')
    }
  }),
  portalCompatible: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "marketplace-assets-ui.assets.filters.portalCompatible.tag"
  }),
  tiers: ({
    value
  }) => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "marketplace-assets-ui.assets.filters.tiers.tag",
    options: {
      value: unescapedText(getTierI18nKey(value))
    }
  }),
  providers: ({
    value
  }) => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "marketplace-assets-ui.assets.filters.providers.tag",
    options: {
      value
    }
  }),
  minOverallRating: ({
    value
  }) => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "marketplace-assets-ui.assets.filters.rating.tag",
    options: {
      count: value
    }
  }),
  hasFreeSupport: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "marketplace-assets-ui.assets.filters.support.tag"
  }),
  tags: ({
    value
  }) => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "marketplace-assets-ui.assets.filters.searchTags.tag",
    options: {
      value
    }
  })
};

function isInFilterTagMap(key) {
  return key in FilterTagMap;
}

function FilterLabel({
  name,
  value
}) {
  if (isInFilterTagMap(name)) {
    const Component = FilterTagMap[name];
    return /*#__PURE__*/_jsx(Component, {
      name: name,
      value: value
    });
  }

  return null;
}

function FilterTag({
  name,
  onClear,
  value
}) {
  const testId = useMemo(() => {
    const testIdPrefix = `filter-tag::${name}`;

    switch (name) {
      case 'businessTypes':
      case 'pageTypes':
      case 'pageFeatures':
      case 'contentTypes':
      case 'tiers':
      case 'providers':
      case 'tags':
        return `${testIdPrefix}::${value}`;

      default:
        return testIdPrefix;
    }
  }, [name, value]);
  return /*#__PURE__*/_jsx(UITag, {
    className: "m-left-0 m-right-2 m-y-1",
    closeable: true,
    "data-test-id": testId,
    inline: true,
    onCloseClick: () => onClear(name, value),
    children: /*#__PURE__*/_jsx(FilterLabel, {
      name: name,
      value: value
    })
  });
}

export function MobileFilterTag({
  name,
  onClear,
  value
}) {
  return /*#__PURE__*/_jsx(MobileTagButton, {
    closeable: true,
    onClick: () => onClear(name, value),
    children: /*#__PURE__*/_jsx(FilterLabel, {
      name: name,
      value: value
    })
  });
}
export default FilterTag;